import {
  Box,
  Typography,
  Divider,
  Stack,
  Button,
  ButtonBase,
  Slider,
  useTheme,
  Paper,
} from "@mui/material";
import {
  HubOutlined,
  AccountTreeOutlined,
  DragIndicatorOutlined,
  Check,
  CancelOutlined,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useStyles from "./styles";

const TreePreview = ({ flipped = false }) => (
  <svg
    width="44"
    height="30"
    viewBox="0 0 44 30"
    fill="none"
    style={flipped ? { transform: "scaleY(-1)" } : undefined}
  >
    <path
      d="M22 8.5v5.5M10 14h24M10 14v7M22 14v7M34 14v7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.55"
    />
    <circle cx="22" cy="5" r="3.5" fill="currentColor" />
    <circle cx="10" cy="24" r="2.5" fill="currentColor" opacity="0.65" />
    <circle cx="22" cy="24" r="2.5" fill="currentColor" opacity="0.65" />
    <circle cx="34" cy="24" r="2.5" fill="currentColor" opacity="0.65" />
  </svg>
);

const DIRECTIONS = [
  {
    label: "Descendente",
    dir: "TB",
    icon: <TreePreview />,
  },
  {
    label: "Ascendente",
    dir: "BT",
    icon: <TreePreview flipped />,
  },
];

const ALGOS = [
  {
    label: "Hierárquico",
    algo: "elk",
    icon: <AccountTreeOutlined sx={{ fontSize: 30 }} />,
  },
  {
    label: "Físico",
    algo: "fd",
    icon: <HubOutlined sx={{ fontSize: 30 }} />,
  },
];

const OptionCard = ({ label, icon, selected, onSelect, styles }) => (
  <ButtonBase
    focusRipple
    aria-pressed={selected}
    onClick={onSelect}
    sx={styles.directionBtn(selected)}
  >
    <Box sx={styles.directionIcon(selected)}>{icon}</Box>
    <Typography sx={styles.directionLabel(selected)}>{label}</Typography>
  </ButtonBase>
);

const AutoLayoutPopover = ({
  anchorEl,
  onClose,
  onAutoLayout,
  onRevertLayout,
  hasLayoutHistory = false,
  onSaveLayout = null,
  loadingSaveLayout = false,
  containerRef = null,
  editMode = true,
}) => {
  const theme = useTheme();

  const [layoutAlgo, setLayoutAlgo] = useState("elk");
  const [layoutDir, setLayoutDir] = useState(null);
  const [layoutSpacing, setLayoutSpacing] = useState({
    nodesep: 80,
    ranksep: 120,
    edgesep: 10,
    portsep: 20,
  });

  const [pos, setPos] = useState(null);
  const dragState = useRef(null);
  const paperRef = useRef(null);

  const styles = useStyles(theme, { layoutDir });

  useEffect(() => {
    if (!anchorEl) {
      setPos(null);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    setPos({ x: rect.right + 12, y: rect.top + rect.height / 2 - 150 });
  }, [anchorEl]);

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const getBounds = () => {
    const paper = paperRef.current;
    const pw = paper ? paper.offsetWidth : 260;
    const ph = paper ? paper.offsetHeight : 500;

    if (containerRef?.current) {
      const cr = containerRef.current.getBoundingClientRect();
      return {
        minX: cr.left,
        minY: cr.top,
        maxX: cr.right - pw,
        maxY: cr.bottom - ph,
      };
    }

    return {
      minX: 0,
      minY: 0,
      maxX: window.innerWidth - pw,
      maxY: window.innerHeight - ph,
    };
  };

  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();

    dragState.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
    };

    const onMove = (e) => {
      if (!dragState.current) return;
      const dx = e.clientX - dragState.current.startMouseX;
      const dy = e.clientY - dragState.current.startMouseY;
      const { minX, minY, maxX, maxY } = getBounds();
      setPos({
        x: clamp(dragState.current.startPosX + dx, minX, maxX),
        y: clamp(dragState.current.startPosY + dy, minY, maxY),
      });
    };

    const onUp = () => {
      dragState.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleDiscard = () => {
    if (hasLayoutHistory && onRevertLayout) onRevertLayout();
    setLayoutDir(null);
    onClose();
  };

  const wasEditModeRef = useRef(editMode);
  useEffect(() => {
    if (wasEditModeRef.current && !editMode) {
      handleDiscard();
    }
    wasEditModeRef.current = editMode;
  }, [editMode]);

  if (!anchorEl || !pos) return null;

  return createPortal(
    <Paper
      ref={paperRef}
      elevation={8}
      sx={{ ...styles.paper, left: pos.x, top: pos.y }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        onMouseDown={handleDragStart}
        sx={styles.dragHandle}
      >
        <DragIndicatorOutlined fontSize="small" sx={styles.dragIcon} />
        <Typography fontWeight={500} sx={styles.title}>
          Organizar layout
        </Typography>
      </Stack>

      <Box sx={{ p: 1.5 }}>
        <Typography sx={{ ...styles.sectionLabel, mb: 0.75 }}>
          Algoritmo
        </Typography>
        <Box sx={styles.directionGroup}>
          {ALGOS.map(({ label, algo, icon }) => (
            <OptionCard
              key={algo}
              label={label}
              icon={icon}
              selected={layoutAlgo === algo}
              styles={styles}
              onSelect={() => {
                setLayoutAlgo(algo);
                setLayoutDir(null);
              }}
            />
          ))}
        </Box>

        <Divider sx={{ borderColor: theme.palette.divider, mb: 1.5 }} />

        {layoutAlgo !== "fd" && (
          <>
            <Typography sx={{ ...styles.sectionLabel, mb: 0.75 }}>
              Orientação
            </Typography>
            <Box sx={styles.directionGroup}>
              {DIRECTIONS.map(({ label, dir, icon }) => (
                <OptionCard
                  key={dir}
                  label={label}
                  icon={icon}
                  selected={layoutDir === dir}
                  styles={styles}
                  onSelect={() => {
                    setLayoutDir(dir);
                    if (onAutoLayout)
                      onAutoLayout(
                        layoutAlgo,
                        dir,
                        layoutSpacing.nodesep,
                        layoutSpacing.ranksep,
                        layoutSpacing.edgesep,
                        layoutSpacing.portsep,
                      );
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ borderColor: theme.palette.divider, mb: 1.5 }} />
          </>
        )}

        {layoutAlgo === "fd" && (
          <>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              startIcon={<HubOutlined fontSize="small" />}
              onClick={() => {
                setLayoutDir("FD");
                if (onAutoLayout)
                  onAutoLayout(
                    "fd",
                    null,
                    layoutSpacing.nodesep,
                    layoutSpacing.ranksep,
                    layoutSpacing.edgesep,
                  );
              }}
              sx={styles.fdBtn}
            >
              Aplicar Force Directed
            </Button>
            <Divider sx={{ borderColor: theme.palette.divider, mb: 1.5 }} />
          </>
        )}

        <Box sx={{ px: 0.5, mb: 1, opacity: layoutDir ? 1 : 0.3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={0.25}
          >
            <Typography sx={styles.sectionLabel}>
              {layoutAlgo === "fd"
                ? "Distância dos links"
                : "Espaçamento entre nós"}
            </Typography>
            <Typography sx={styles.spacingValue}>
              {layoutSpacing.nodesep}px
            </Typography>
          </Stack>
          <Slider
            size="small"
            min={20}
            max={500}
            step={10}
            value={layoutSpacing.nodesep}
            disabled={!layoutDir}
            onChange={(_, v) =>
              setLayoutSpacing((prev) => ({ ...prev, nodesep: v }))
            }
            onChangeCommitted={(_, v) => {
              if (layoutDir && onAutoLayout) {
                const dir = layoutDir === "FD" ? null : layoutDir;
                onAutoLayout(
                  layoutAlgo,
                  dir,
                  v,
                  layoutSpacing.ranksep,
                  layoutSpacing.edgesep,
                  layoutSpacing.portsep,
                );
              }
            }}
            sx={styles.slider}
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={0.25}
            mt={0.5}
          >
            <Typography sx={styles.sectionLabel}>
              {layoutAlgo === "fd"
                ? "Força de repulsão"
                : "Espaçamento entre níveis"}
            </Typography>
            <Typography sx={styles.spacingValue}>
              {layoutSpacing.ranksep}px
            </Typography>
          </Stack>
          <Slider
            size="small"
            min={40}
            max={400}
            step={10}
            value={layoutSpacing.ranksep}
            disabled={!layoutDir}
            onChange={(_, v) =>
              setLayoutSpacing((prev) => ({ ...prev, ranksep: v }))
            }
            onChangeCommitted={(_, v) => {
              if (layoutDir && onAutoLayout) {
                const dir = layoutDir === "FD" ? null : layoutDir;
                onAutoLayout(
                  layoutAlgo,
                  dir,
                  layoutSpacing.nodesep,
                  v,
                  layoutSpacing.edgesep,
                  layoutSpacing.portsep,
                );
              }
            }}
            sx={styles.slider}
          />

          {layoutAlgo !== "fd" && (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={0.25}
                mt={0.5}
              >
                <Typography sx={styles.sectionLabel}>
                  Distância entre linhas
                </Typography>
                <Typography sx={styles.spacingValue}>
                  {layoutSpacing.edgesep}px
                </Typography>
              </Stack>
              <Slider
                size="small"
                min={10}
                max={200}
                step={10}
                value={layoutSpacing.edgesep}
                disabled={!layoutDir}
                onChange={(_, v) =>
                  setLayoutSpacing((prev) => ({ ...prev, edgesep: v }))
                }
                onChangeCommitted={(_, v) => {
                  if (layoutDir && onAutoLayout) {
                    onAutoLayout(
                      layoutAlgo,
                      layoutDir,
                      layoutSpacing.nodesep,
                      layoutSpacing.ranksep,
                      v,
                      layoutSpacing.portsep,
                    );
                  }
                }}
                sx={styles.slider}
              />

              {/* <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={0.25}
                mt={0.5}
              >
                <Typography sx={styles.sectionLabel}>
                  Distância na saída do nó
                </Typography>
                <Typography sx={styles.spacingValue}>
                  {layoutSpacing.portsep}px
                </Typography>
              </Stack> */}
              {/* <Slider
                size="small"
                min={5}
                max={80}
                step={5}
                value={layoutSpacing.portsep}
                onChange={(_, v) =>
                  setLayoutSpacing((prev) => ({ ...prev, portsep: v }))
                }
                onChangeCommitted={(_, v) => {
                  if (layoutDir && onAutoLayout) {
                    onAutoLayout(
                      layoutAlgo,
                      layoutDir,
                      layoutSpacing.nodesep,
                      layoutSpacing.ranksep,
                      layoutSpacing.edgesep,
                      v,
                    );
                  }
                }}
                sx={styles.slider}
              /> */}
            </>
          )}
        </Box>

        <Divider sx={{ borderColor: theme.palette.divider, mt: 0.5, mb: 1 }} />
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="text"
            fullWidth
            onClick={handleDiscard}
            startIcon={<CancelOutlined />}
            sx={styles.discardBtn}
          >
            Descartar
          </Button>
          <Button
            size="small"
            variant="contained"
            fullWidth
            disabled={loadingSaveLayout || !hasLayoutHistory}
            onClick={async () => {
              if (onSaveLayout) await onSaveLayout();
              onClose();
            }}
            startIcon={<Check />}
            sx={styles.saveBtn}
          >
            {loadingSaveLayout ? "Salvando..." : "Salvar"}
          </Button>
        </Stack>
      </Box>
    </Paper>,
    document.body,
  );
};

export default AutoLayoutPopover;
